"use client";

import { createContext, type ReactNode, useContext } from "react";
import {
  Radio as RACRadio,
  RadioGroup as RACRadioGroup,
  RadioGroupProps as RACRadioGroupProps,
  RadioProps,
  ValidationResult,
} from "react-aria-components";
import { composeTailwindRenderProps, focusRing } from "../../utils";
import { Description, FieldError, Label } from "../Field";

type SelectionVariant = "primary";
type LegacySelectionVariant = "default" | "brand";

function normalizeSelectionVariant(_variant: SelectionVariant | LegacySelectionVariant | undefined): SelectionVariant {
  return "primary";
}

// Context to pass variant to Radio components
const RadioGroupContext = createContext<{ variant: SelectionVariant }>({
  variant: "primary",
});

export interface RadioGroupProps extends Omit<RACRadioGroupProps, "children"> {
  /** Label for the radio group */
  label?: ReactNode;
  /** Children elements */
  children?: ReactNode;
  /** Optional description for the radio group */
  description?: string;
  /** Error message to display when validation fails */
  errorMessage?: string | ((validation: ValidationResult) => string);
  /** Validation result object for functional errorMessage */
  validationResult?: ValidationResult;
  /**
   * Deprecated compatibility prop. RadioGroup now has one selected treatment; `default` and `brand` both map to primary.
   * @default "primary"
   */
  variant?: SelectionVariant | LegacySelectionVariant;
}

export function RadioGroup(props: RadioGroupProps) {
  const { variant = "primary", ...otherProps } = props;
  const normalizedVariant = normalizeSelectionVariant(variant);
  return (
    <RadioGroupContext.Provider value={{ variant: normalizedVariant }}>
      <RACRadioGroup
        {...otherProps}
        className={composeTailwindRenderProps(props.className, "group flex flex-col gap-2")}
      >
        <Label>{props.label}</Label>
        <div className={`flex ${otherProps.orientation === "horizontal" ? "flex-row gap-4" : "flex-col gap-2"}`}>
          {props.children}
        </div>
        {props.description && <Description>{props.description}</Description>}
        <FieldError>
          {typeof props.errorMessage === "function" && props.validationResult
            ? props.errorMessage(props.validationResult)
            : typeof props.errorMessage === "string"
              ? props.errorMessage
              : null}
        </FieldError>
      </RACRadioGroup>
    </RadioGroupContext.Provider>
  );
}

function getRadioStyles({
  isSelected,
  isInvalid,
  isDisabled,
}: {
  isSelected?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
}) {
  const focusRingColor = "focus-visible:outline-action-primary";
  const baseClasses = [
    focusRing(),
    focusRingColor,
    "w-5 h-5 rounded-full border-2 bg-background-surface transition-all",
  ];

  if (isDisabled) {
    baseClasses.push("border-border-muted forced-colors:!border-[GrayText]");
  } else if (isInvalid) {
    baseClasses.push(
      "border-feedback-error-border group-pressed:border-feedback-error-border dark:group-pressed:border-feedback-error-border forced-colors:!border-[Mark]"
    );
  } else if (isSelected) {
    baseClasses.push(
      "border-[7px] border-action-primary forced-colors:!border-[Highlight] group-pressed:border-action-primary-hover dark:group-pressed:border-action-primary-hover"
    );
  } else {
    baseClasses.push("border-border-default group-pressed:border-border-focus dark:group-pressed:border-border-focus");
  }

  return baseClasses.join(" ");
}

export function Radio(props: RadioProps) {
  useContext(RadioGroupContext);
  return (
    <RACRadio
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        // `relative` contains react-aria's visually-hidden <input> — see Checkbox.tsx getCheckboxStyles.
        "relative group flex items-center gap-2 text-sm text-text-primary transition disabled:text-text-secondary dark:text-text-primary dark:disabled:text-text-secondary forced-colors:disabled:text-[GrayText]"
      )}
    >
      {(renderProps) => (
        <>
          <div className={getRadioStyles(renderProps)} />
          {props.children}
        </>
      )}
    </RACRadio>
  );
}
